import { ChevronRight } from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { XStack, YStack } from "tamagui";
import { formatDate } from "../../../core/dayJs";
import { isBeforeToday } from "../../../core/utils";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { Body } from "../../../ui/Body";
import { Label } from "../../../ui/Label";
import { Subtext } from "../../../ui/Subtext";
import type BusinessModuleModel from "../../modules/BusinessModuleModel";
import { useLoadBusinessPlanModules } from "../../modules/ui/BusinessTasksList";
import { isEvent, isEventModule, isEventTeamModule } from "../../scheduleTypes";
import type { BusinessPlanModel } from "../BusinessPlanModel";
import { useScheduleItems } from "../tabs/useScheduleItems";
import { BusinessIcon } from "./BusinessIcon";

function _isModuleWithCalculatedDate(
  // biome-ignore lint/suspicious/noExplicitAny: Because
  module: BusinessModuleModel<any, any>,
  // biome-ignore lint/suspicious/noExplicitAny: Because
): module is BusinessModuleModel<any, any> & { calculatedDate: Timestamp } {
  return (
    "calculatedDate" in module && module.calculatedDate instanceof Timestamp
  );
}

export const BusinessNextUp: React.FC<{ businessPlan: BusinessPlanModel }> =
  observer(({ businessPlan }) => {
    const { navigate } = usePlanNavigation();
    const { t } = useTranslation();
    useLoadBusinessPlanModules(businessPlan);
    const items = useScheduleItems(businessPlan.uid).list;
    const nextUp = items.find((item) => {
      if (isEvent(item)) {
        return true;
      }
      if (isEventTeamModule(item)) {
        return !item.module.isCompletedByUser;
      }
      if (isEventModule(item)) {
        return !item.module.isCompletedByUser;
      }

      console.error("invalid type in Next Up", item);
      return false;
    });

    if (!nextUp) {
      return null;
    }

    const date = new Date(nextUp.date);
    const isLate = isBeforeToday(new Date(nextUp.date));

    return (
      <XStack
        alignItems="center"
        justifyContent="space-between"
        backgroundColor={"$pressablePrimaryBackground"}
        paddingLeft={"$large"}
        paddingRight={"$medium"}
        paddingVertical={"$medium"}
        cursor="pointer"
        onPress={() => {
          switch (nextUp.type) {
            case "item":
              navigate("BusinessSchedule", {
                businessUid: businessPlan.uid,
              });
              break;
            case "module":
              navigate("BusinessTasks", {
                businessUid: businessPlan.uid,
              });
              break;
            case "team-module":
              navigate("BusinessTeam", {
                businessUid: businessPlan.uid,
              });
              break;

            default:
          }
        }}
      >
        <YStack
          gap="$small"
          flex={1}
          backgroundColor={"$pressablePrimaryBackground"}
        >
          {isLate && (
            <Subtext color={"$pressablePrimaryLabel"}>Deadline missed!</Subtext>
          )}
          <Subtext color={"$pressablePrimaryLabel"}>
            {t("business:next_up")}
            {date ? ` - ${formatDate(date)}` : ""}
          </Subtext>
          <XStack gap={"$small"} alignItems="center">
            <BusinessIcon
              type={nextUp.type === "team-module" ? "team" : "customer"}
              color={"$pressablePrimaryLabel"}
            />
            <Label color={"$pressablePrimaryLabel"} numberOfLines={1}>
              {nextUp.title}
            </Label>
          </XStack>
        </YStack>
        <XStack gap="$small" alignItems="center">
          <Body color={"$pressablePrimaryLabel"}> {t("business:lets_go")}</Body>
          <ChevronRight color={"$pressablePrimaryLabel"} />
        </XStack>
      </XStack>
    );
  });
